<template>
  <div class="edit-obituary-comment__edit-content create-comment-form">
    <div class="create-comment-form__container">
      <div class="comment-input">
        <label for="comment-greeting">{{
          `${texts.editComment.messageLabel}*`
        }}</label>
        <textarea
          id="comment-greeting"
          rows="4"
          v-model="commentContent"
          :disabled="uploading"
          :placeholder="
            sprintf(
              `${texts.common.condolenceMessagePlaceHolder}*`,
              deceasedName
            )
          "
        />
      </div>

      <div class="name-input">
        <label for="comment-author">{{ `${texts.common.yourName}*` }}</label>
        <input
          id="comment-author"
          type="text"
          v-model="commentName"
          :disabled="uploading"
          :placeholder="texts.common.yourName"
        >
      </div>
    </div>

    <div class="create-comment-form-thumbnails" v-if="editableImages.length">
      <p
        class="create-comment-form-thumbnails__description"
        v-if="timelineEnabled"
        v-text="uploadDescription"
      />

      <div
        class="create-comment-form-thumbnails__item"
        v-for="(image, index) in editableImages"
        :key="image.src"
      >
        <div class="create-comment-form-thumbnails__thumbnail">
          <ix-img
            class="create-comment-form-thumbnails__image"
            height="80"
            width="80"
            :imgix-params="{ fit: 'crop', h: 80, w: 80 }"
            :src="image.src"
          />

          <button
            class="create-comment-form-thumbnails__item-remove btn outline"
            type="button"
            :title="texts.editComment.removeImage"
            @click.prevent="removeImage(index)"
          >
            <svg
              aria-hidden="true"
              class="icon create-comment-form-thumbnails__item-xmark"
            >
              <use href="#xmark" />
            </svg>
          </button>
        </div>

        <div
          class="create-comment-form-thumbnails__item-meta"
          v-if="timelineEnabled"
        >
          <YearSelect
            :model-value="image.yearTaken"
            @update:model-value="updateImageYear(index, $event)"
          />

          <button
            class="create-comment-form-thumbnails__add-caption btn link"
            type="button"
            v-if="!showCaptionForIndex.includes(index)"
            @click="showCaptionForIndex.push(index)"
            v-text="texts.createComment.addImageCaption"
          />
        </div>

        <textarea
          class="create-comment-form-thumbnails__caption-meta"
          rows="2"
          v-if="timelineEnabled && showCaptionForIndex.includes(index)"
          :placeholder="texts.createComment.captionPlaceholder"
          :value="image.text"
          @input="updateImageText(index, $event.target.value)"
        />
      </div>
    </div>

    <div class="edit-obituary-comment__button-group">
      <button
        class="btn btn-cta"
        type="button"
        :disabled="missingRequired || isNotEdited"
        @click.prevent="editComment"
        v-text="texts.editComment.save"
      />

      <button
        class="btn link"
        type="button"
        @click.prevent="toggleModal"
        v-text="texts.common.cancel"
      />

      <button
        class="btn danger"
        type="button"
        @click.prevent="openDeleteCommentModal"
      >
        <svg aria-hidden="true" class="icon"><use href="#trash" /></svg>
        <span v-text="texts.common.delete" />
      </button>
    </div>
  </div>
</template>

<script>
  import { sprintf } from "sprintf-js";
  import YearSelect from "./YearSelect.vue";

  export default {
    components: { YearSelect },

    computed: {
      isNotEdited() {
        const textNotEdited =
          this.commentName === this.comment.name &&
          this.commentContent === this.comment.comment;

        if (!textNotEdited) {
          return false;
        }

        if (this.removedSrcs.length) {
          return false;
        }

        const originalImages = this.comment.mediaImages || [];

        if (this.editableImages.length !== originalImages.length) {
          return false;
        }

        return this.editableImages.every((img, index) => {
          const original = originalImages[index];
          return (
            img.yearTaken === original.yearTaken && img.text === original.text
          );
        });
      },
      missingRequired() {
        return !(this.commentName && this.commentContent);
      },

      uploadDescription() {
        return sprintf(
          this.texts.createComment.uploadDescription,
          this.possessiveName
        );
      },
    },

    data() {
      return {
        commentContent: this.comment.comment,
        commentName: this.comment.name,
        editableImages: JSON.parse(
          JSON.stringify(this.comment.mediaImages || [])
        ),
        removedSrcs: [],
        showCaptionForIndex: (this.comment.mediaImages || [])
          .map((img, index) => (img.text ? index : null))
          .filter((index) => index !== null),
        texts: lavendlaObituary,
        uploading: false,
      };
    },

    inject: {
      timelineEnabled: {
        default: false,
      },
    },

    methods: {
      editComment() {
        if (this.missingRequired || this.isNotEdited) {
          return;
        }

        this.uploading = true;

        const data = {
          hash: this.hash,
          id: this.comment.id,
        };

        if (this.commentName !== this.comment.name) {
          data.name = this.commentName;
        }

        if (this.commentContent !== this.comment.comment) {
          data.comment = this.commentContent;
        }

        if (this.editableImages.length) {
          data.mediaImages = this.editableImages;
        }

        if (this.removedSrcs.length) {
          data.removedImages = this.removedSrcs;
        }

        this.$store
          .dispatch("comments/updateObituaryComment", data)
          .then(() => {
            this.uploading = false;
            this.toggleModal();
          })
          .catch((error) => {
            this.uploading = false;
            if (error.serverMessage) {
              // biome-ignore lint/suspicious/noAlert: no in-page error UI exists for this form yet
              alert(error.serverMessage);
            } else {
              // biome-ignore lint/suspicious/noAlert: no in-page error UI exists for this form yet
              alert(this.texts.moreCommentActions.alert);
            }
          });
      },

      openDeleteCommentModal() {
        this.changeModalContext("delete");
      },

      removeImage(index) {
        const [removed] = this.editableImages.splice(index, 1);

        if (removed?.src) {
          this.removedSrcs.push(removed.src);
        }

        // Indices shift down past the removed item.
        this.showCaptionForIndex = this.showCaptionForIndex
          .filter((i) => i !== index)
          .map((i) => (i > index ? i - 1 : i));
      },

      sprintf,

      updateImageText(index, text) {
        this.editableImages[index].text = text;
      },

      updateImageYear(index, year) {
        this.editableImages[index].yearTaken = year;
      },
    },
    name: "EditCommentContent",

    props: {
      changeModalContext: {
        required: true,
        type: Function,
      },
      comment: {
        required: true,
        type: Object,
      },
      deceasedName: {
        required: true,
        type: String,
      },
      hash: {
        required: true,
        type: String,
      },
      possessiveName: {
        default: "",
        type: String,
      },
      toggleModal: {
        required: true,
        type: Function,
      },
    },
  };
</script>
